import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  ChevronRight,
  ChevronLeft,
  Archive,
  CreditCard,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../../store/slices/authSlice";
import styles from "./OrderHistory.module.css";

export default function OrdersHistory() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");

  const TABS = [
    { key: "pending_supplier", label: t("orders.tabs.pending") },
    { key: "accepted", label: t("orders.tabs.accepted") },
    { key: "rejected", label: t("orders.tabs.rejected") },
  ];

  const STATUS_MAP = {
    pending_supplier: {
      label: t("orders.status.pending"),
      color: "#f97316",
      bg: "rgba(249,115,22,0.1)",
    },
    accepted: {
      label: t("orders.status.accepted"),
      color: "#0c6839",
      bg: "rgba(12,104,57,0.1)",
    },
    rejected: {
      label: t("orders.status.rejected"),
      color: "#ef4444",
      bg: "rgba(239,68,68,0.1)",
    },
  };

  const [activeTab, setActiveTab] = useState("pending_supplier");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payLoadingId, setPayLoadingId] = useState(null);
  const [payErrors, setPayErrors] = useState({});

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const res = await api.get("/supplier-materials/purchase-orders", {
          params: { status: activeTab },
        });
        if (res.data.code === 200) {
          setOrders(res.data.data || []);
        }
      } catch (err) {
        console.error("Orders fetch error:", err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [activeTab]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toISOString().split("T")[0];
  };

  const formatPrice = (val) => {
    if (!val) return "—";
    const formatted = parseFloat(val).toLocaleString("en-EG", {
      minimumFractionDigits: 2,
    });
    return `${formatted} EGP`;
  };

  const canPay = (order) => order.status === "accepted" && !order.is_paid;

  const handlePayNow = async (order) => {
    setPayLoadingId(order.id);
    setPayErrors((prev) => ({ ...prev, [order.id]: null }));
    try {
      const res = await api.post(
        `/supplier-materials/purchase-orders/${order.id}/fawaterak/init`,
      );
      if (res.data.code === 200) {
        const url =
          res.data.data?.payment_data?.redirectTo ||
          res.data.data?.checkout_url;
        if (url) {
          window.open(url, "_blank");
        } else {
          setPayErrors((prev) => ({
            ...prev,
            [order.id]: t(
              "orders.payErrorGeneric",
              "Could not open the payment page. Please try again.",
            ),
          }));
        }
      }
    } catch (err) {
      const backendMessage = err.response?.data?.message;
      setPayErrors((prev) => ({
        ...prev,
        [order.id]:
          backendMessage ||
          t(
            "orders.payErrorGeneric",
            "Could not start the payment. Please try again.",
          ),
      }));
    } finally {
      setPayLoadingId(null);
    }
  };

  const payBtnStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: "700",
    color: "#ffffff",
    background: "#0c6839",
    border: "none",
    borderRadius: "10px",
    padding: "8px 14px",
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  };

  return (
    <div className={styles.page} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.pageHeader}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          {isRtl ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
        </button>
        <h1 className={styles.pageTitle}>{t("orders.title")}</h1>
      </div>

      <div className={styles.tabsWrap}>
        <div className={styles.tabs}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.skeletonList}>
            {[...Array(3)].map((_, i) => (
              <div key={i} className={styles.skeleton} />
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className={styles.empty}>
            <Archive size={48} strokeWidth={1.2} color="#d1d5db" />
            <p>
              {t("orders.noOrders", {
                status: TABS.find((t) => t.key === activeTab)?.label,
              })}
            </p>
          </div>
        ) : (
          <div className={styles.list}>
            {orders.map((order) => {
              const status =
                STATUS_MAP[order.status] || STATUS_MAP.pending_supplier;
              const showPay = canPay(order);
              const isPaying = payLoadingId === order.id;
              return (
                <div key={order.id} className={styles.card}>
                  <div className={styles.cardTop}>
                    <div className={styles.cardLeft}>
                      <div className={styles.orderIcon}>
                        <Archive size={20} color="#0c6839" />
                      </div>
                      <div style={{ textAlign: "left" }}>
                        {" "}
                        <p className={styles.orderId}>
                          #{order.id ? `PO-${order.id}` : "—"}
                        </p>
                        <p className={styles.orderDate}>
                          <Calendar size={12} />
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                    </div>
                    <span
                      className={styles.statusBadge}
                      style={{ color: status.color, background: status.bg }}
                    >
                      {status.label}
                    </span>
                  </div>

                  <div className={styles.divider} />

                  <div className={styles.cardBottom}>
                    <div>
                      <p className={styles.totalLabel}>{t("orders.total")}</p>
                      <p className={styles.totalValue}>
                        {formatPrice(order.estimated_subtotal)}
                      </p>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {showPay && (
                        <button
                          style={payBtnStyle}
                          disabled={isPaying}
                          onClick={() => handlePayNow(order)}
                        >
                          <CreditCard size={14} />
                          {isPaying
                            ? t("orders.processing", "Processing...")
                            : t("orders.payNow", "Pay Now")}
                        </button>
                      )}
                      <button
                        className={styles.detailBtn}
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        {t("orders.viewDetails")}
                        {isRtl ? (
                          <ChevronLeft size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </button>
                    </div>
                  </div>

                  {payErrors[order.id] && (
                    <div
                      style={{
                        marginTop: "10px",
                        background: "rgba(239,68,68,0.08)",
                        border: "1px solid rgba(239,68,68,0.25)",
                        color: "#b91c1c",
                        fontSize: "12.5px",
                        fontWeight: "600",
                        borderRadius: "8px",
                        padding: "9px 12px",
                      }}
                    >
                      {payErrors[order.id]}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
